import { download as downloader } from "~/Scripts/resources/downloadsResource";
import debounce from "lodash/debounce";

const DownloadState = Object.freeze( {
  IDLE: "Idle",
  IN_PROGRESS: "In Progress",
  COMPLETED: "Completed",
  FAILED: "Failed"
} );

const idleDelay = 2000;

export default {
  props: {
    targetFileName: {
      type: String,
      default: "DES_Resource_Pack"
    }
  },
  data: () => ( {
    downloadState: DownloadState.IDLE
  } ),
  methods: {
    downloadFile ( url, filename, ext ) {
      this.downloadState = DownloadState.IN_PROGRESS;
      try {
        const downloadLink = this.createDownloadLink( url, filename, ext );
        downloadLink.click();
        document.body.removeChild( downloadLink );
      } catch ( error ) {
        this.handleDownloadError( error );
      } finally {
        this.downloadState = DownloadState.COMPLETED;
        this.setDelayedIdleState();
      }
    },
    downloadFiles ( downloads ) {
      return new Promise( ( resolve, reject ) => {
        if ( downloads.length === 0 ) {
          this.showNoSelectionToast();
          reject( new Error( "No downloads selected." ) );
          return;
        }

        this.downloadState = DownloadState.IN_PROGRESS;

        try {
          const files = downloads.map( ( { name, url, extension } ) => {
            if ( !extension || extension === "" ) {
              extension = this.getExtFromUrl( url );
            }
            return {
              url: this.formatDownloadUrl( url ),
              fileName: this.formatFileName( name, extension ),
              contentType: this.getContentType( extension )
            };
          } );
          downloader( { files, targetFileName: this.targetFileName } )
            .then( ( response ) => {
              this.handleSuccessfulDownloadResponse( response );
              resolve();
            } )
            .catch( ( error ) => {
              this.handleDownloadError( error );
              reject( error );
            } )
            .finally( () => {
              this.downloadState = DownloadState.COMPLETED;
              this.setDelayedIdleState();
            } );
        } catch ( ex ) {
          this.handleDownloadError( ex );
          reject( ex );
        }
      } );
    },

    createDownloadLink ( url, filename, ext ) {
      const download = {
        url: this.formatDownloadUrl( url ),
        name: this.formatFileName( filename || this.getFileNameFromUrl( url ), ext )
      };

      const link = document.createElement( "a" );
      link.href = download.url;
      link.download = download.name;
      document.body.appendChild( link );
      return link;
    },

    isRelativeUrl ( url ) {
      if ( typeof url !== "string" || url.trim() === "" ) {
        return false;
      }

      return /^(?![a-zA-Z][a-zA-Z\d+\-.]*:).*/.test( url );
    },
    isAbsoluteUrl ( url ) {
      if ( typeof url !== "string" || !url ) {
        return false;
      }

      return /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test( url );
    },

    formatFileName ( fileName, ext ) {
      if ( typeof fileName !== "string" || typeof ext !== "string" ) {
        throw new Error( "Parameters must be strings." );
      }

      const formattedExt = ext.startsWith( "." ) ? ext : `.${ext}`;

      if ( fileName.match( new RegExp( `${formattedExt}$`, "i" ) ) ) {
        return fileName;
      }

      return `${fileName}${formattedExt}`;
    },
    formatDownloadUrl ( url ) {
      if ( this.isAbsoluteUrl( url ) ) {
        return url;
      }
      if ( this.isRelativeUrl( url ) ) {
        return new URL( url, window.location.origin ).href;
      }

      throw new Error( "Invalid URL provided for download." );
    },

    getExtFromUrl ( url ) {
      const { pathname } = new URL( url );
      const lastSegment = pathname.split( "/" ).pop();
      const extension = lastSegment.includes( "." ) ? `.${lastSegment.split( "." ).pop().toLowerCase()}` : "";
      return extension || "";
    },

    getContentType ( ext ) {
      if ( !ext.startsWith( "." ) ) {
        ext = `.${ext}`;
      }

      const mimeTypes = {
        ".pdf": "application/pdf",
        ".jpg": "image/jpeg",
        ".jpeg": "image/jpeg",
        ".png": "image/png",
        ".gif": "image/gif",
        ".txt": "text/plain",
        ".html": "text/html",
        ".css": "text/css",
        ".js": "application/javascript",
        ".zip": "application/zip",
        ".csv": "text/csv",
        ".json": "application/json",
        // Microsoft Office
        ".doc": "application/msword",
        ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ".xls": "application/vnd.ms-excel",
        ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ".ppt": "application/vnd.ms-powerpoint",
        ".pptx": "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        // Open Document Formats
        ".odt": "application/vnd.oasis.opendocument.text",
        ".ods": "application/vnd.oasis.opendocument.spreadsheet",
        ".odp": "application/vnd.oasis.opendocument.presentation",
        // Linux Documents
        ".tex": "application/x-tex",
        ".xml": "application/xml",
        // macOS Documents
        ".pages": "application/vnd.apple.pages",
        ".numbers": "application/vnd.apple.numbers",
        ".key": "application/vnd.apple.keynote",
        // Other common formats
        ".svg": "image/svg+xml",
        ".mp3": "audio/mpeg",
        ".mp4": "video/mp4",
        ".wav": "audio/wav",
        ".avi": "video/x-msvideo",
        ".mov": "video/quicktime",
        ".apk": "application/vnd.android.package-archive",
        ".exe": "application/vnd.microsoft.portable-executable"
      };

      return mimeTypes[ext.toLowerCase()] || "application/octet-stream";
    },
    getFileNameFromUrl ( url ) {
      return url.split( "/" ).pop() || this.targetFileName;
    },
    getProductDownloadIcon ( ext = "" ) {
      let result;

      switch ( ext?.toUpperCase() ) {
        case "JPG":
        case "JPEG":
        case "BMP":
        case "SVG":
        case "PSD":
        case "AI":
        case "PNG":
          result = "file-image";
          break;

        case "PDF":
          result = "file-pdf";
          break;

        case "XLS":
        case "XLSX":
        case "CSV":
        case "NUMBERS":
          result = "file-spreadsheet";
          break;

        case "DOC":
        case "DOCX":
        case "RTF":
        case "PAGES":
        case "ODT":
        case "ODF":
        case "MD":
        case "MARKDOWN":
        case "MK":
        case "MKD":
        case "TXT":
        case "EPUB":
        case "IBOOK":
          result = "file-alt";
          break;

        case "PPT":
        case "PPTX":
        case "KEY":
        case "KEYNOTE":
          result = "file-chart-pie";
          break;

        case "MP4":
        case "AVI":
        case "MKV":
        case "MOV":
        case "WMV":
        case "M4A":
        case "WEBM":
        case "AMV":
          result = "file-video";
          break;

        case "MP3":
        case "AAC":
        case "WAV":
        case "FLAC":
        case "ALAC":
        case "OGG":
        case "WMA":
          result = "file-music";
          break;

        case "ZIP":
        case "RAR":
        case "7Z":
        case "GZ":
        case "TAR":
          result = "file-archive";
          break;

        default:
          result = "file";
          break;
      }

      return result;
    },

    handleDownloadError ( error ) {
      const message = typeof error === "string"
        ? error
        : error.message || "An unknown error occurred.";
      this.downloadState = DownloadState.FAILED;
      this.showDownloadFailedToast( message );
    },
    handleSuccessfulDownloadResponse ( response ) {
      const blob = new Blob( [response.data] );
      const url = window.URL.createObjectURL( blob );
      const a = document.createElement( "a" );
      a.href = url;

      // extract filename from the content-disposition header
      const contentDisposition = response.headers["content-disposition"];
      let fileName = this.targetFileName;
      if ( contentDisposition ) {
        const matches = contentDisposition.match( /filename="(.+)"/ );
        if ( matches ) {
          [fileName] = matches.slice( 1 );
        }
      }

      a.download = ( fileName.endsWith( ".zip" ) ? fileName : `${fileName}.zip` ).replace( /[:<>?*"|]/g, "_" );
      document.body.appendChild( a );
      a.click();
      a.remove();
      window.URL.revokeObjectURL( url );
    },

    showDownloadFailedToast ( logErrorMessage ) {
      if ( logErrorMessage ) {
        console.error( logErrorMessage );
      }

      const toastMessage = "There was an issue with your download. Please, try again or contact us.";

      this.$bvToast.toast( toastMessage, {
        title: "Download failed.",
        autoHideDelay: 8000,
        appendToast: true,
        variant: "danger"
      } );
    },
    showNoSelectionToast () {
      const toastMessage = `Please check the boxes for the items you want to download, and then click the "${this.downloadSelectedBtnLabel}" button.`;

      this.$bvToast.toast( toastMessage, {
        title: "No download options selected",
        autoHideDelay: 8000,
        appendToast: true,
        variant: "warning"
      } );
    },

    setDelayedIdleState () {
      setTimeout( () => {
        this.setDownloadState( DownloadState.IDLE );
      }, idleDelay );
    },
    setDownloadState: debounce( function( state ) {
      this.downloadState = state;
    }, idleDelay )
  },
  computed: {
    isDownloadInProgress () {
      return this.downloadState === DownloadState.IN_PROGRESS;
    },
    hasDownloadCompleted () {
      return this.downloadState === DownloadState.COMPLETED;
    },
    hasDownloadFailed () {
      return this.downloadState === DownloadState.FAILED;
    }
  }
};
